import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import Input from '@/components/Input';
import Loading from '@/components/Loading';
import MultiSelect from '@/components/MultiSelect';
import { Button } from '@/components/Button';
import useFetchActiveUsers from '@/features/agent/api/useFetchActiveUsers';
import { candidateResumeParseResultAtom } from '../atom/candidatesAtom';
import useFetchCandidateProfilesCustomFields from '../api/useFetchCandidateProfilesCustomFields';
import usePostCandidateProfile from '../api/usePostCandidateProfile';
import useAttachFilesFromAnswer from '../api/useAttachFilesFromAnswer';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import CustomFieldsForm from './CustomFieldsForm';
import ResumeUploadSection from './ResumeUploadSection';
import {
  createCandidatesProfileAddSchema,
  type CandidatesProfileAddSchema,
} from '../schemas/candidatesProfileAddSchema';
import type { CandidateProfilePostRequest } from '../types/CandidateProfile';
import {
  areRequiredCustomFieldsFilled,
  type CustomFieldValue,
} from '@/utils/customFieldValidation';
import type {
  AgentApiCandidateFormAnswersItem,
  AgentApiCandidateProfilesParseDocument,
} from '@/types/serializers';
import { groupCustomFieldsByCategory } from '@/utils/customFieldGrouping';
import { buildCustomDataPayload } from '@/utils/customDataForm';

/** API のフォーム回答1項目（AgentApiCandidateFormAnswersItem のエイリアス） */
export type FormAnswerItem = AgentApiCandidateFormAnswersItem;

type Props = {
  /** 作成成功時に呼ばれる（モーダルでは onClose + toast を渡す） */
  onSuccess?: (createdId: number) => void;
  /** モーダル内のキャンセル時に呼ばれる（embedInModal 時のみ使用） */
  onCancel?: () => void;
  /** モーダル内に埋め込む場合は true（送信ボタンをインライン表示） */
  embedInModal?: boolean;
  /** フォーム回答から開いた場合の項目一覧（item_type / custom_field_id で初期値にマッピング） */
  initialFromAnswer?: FormAnswerItem[];
  /** フォーム回答ID（指定時は answer のファイル項目のファイルを document_files にアタッチする） */
  candidateFormAnswerId?: number;
  /**
   * 書類から作成モードを初期表示するか。
   * true の場合、フォーム上部に書類アップロードセクションを表示する。
   */
  showResumeUpload?: boolean;
};

const EMPTY_DEFAULTS: Pick<CandidatesProfileAddSchema, 'name' | 'customData'> = {
  name: '',
  customData: {},
};

/**
 * AnswerItem を FormItem の item_type / custom_field_id に基づき氏名・custom_data にマッピングする。
 * - item_type === 'candidate_name' → 氏名
 * - item_type === 'custom_field' かつ custom_field_id → customData[custom_field_id]
 */
function buildDefaultValuesFromAnswerItems(
  items: AgentApiCandidateFormAnswersItem[] | undefined
): Pick<CandidatesProfileAddSchema, 'name' | 'customData'> {
  if (!items?.length) return EMPTY_DEFAULTS;
  const normalized = (v: string) => (v === '未入力' ? '' : v);
  const nameItem = items.find((i) => i.item_type === 'candidate_name');
  const name = nameItem ? normalized(nameItem.value) : '';
  const customData: Record<string, string | number | string[] | undefined> = {};
  for (const item of items) {
    if (item.item_type === 'custom_field' && item.custom_field_id != null) {
      const val = normalized(item.value);
      customData[String(item.custom_field_id)] = val || undefined;
    }
  }
  return { name, customData };
}

export default function CandidateProfileAddForm({
  onSuccess,
  onCancel,
  embedInModal = false,
  initialFromAnswer,
  candidateFormAnswerId,
  showResumeUpload = false,
}: Props) {
  const { data: customFields, isLoading: isCustomFieldsLoading } =
    useFetchCandidateProfilesCustomFields();
  const { data: activeUsersData, isLoading: isActiveUsersLoading } = useFetchActiveUsers();
  const { mutateAsync } = usePostCandidateProfile();
  const { mutateAsync: attachFilesFromAnswer } = useAttachFilesFromAnswer();

  /** 「ファイルから登録」モーダルから引き継いだ書類解析結果（プリフィル用、反映後は null にクリアする） */
  const [resumeParseResult, setResumeParseResult] = useAtom(candidateResumeParseResultAtom);

  /** 書類から作成モード: 解析済みblobのsigned_id一覧（モーダル経由は最大2件、インラインアップロードは1件） */
  const [parsedBlobSignedIds, setParsedBlobSignedIds] = useState<string[]>([]);

  const isLoading = isCustomFieldsLoading || isActiveUsersLoading;

  /** AnswerItem の item_type / custom_field_id に対応する氏名・custom_data をデフォルト値としてセット */
  const defaultValues = useMemo(
    () => buildDefaultValuesFromAnswerItems(initialFromAnswer),
    [initialFromAnswer]
  );

  // 必須カスタムフィールドの検証を含めるため、取得した customFields からスキーマを動的生成する
  const schema = useMemo(
    () => createCandidatesProfileAddSchema(customFields?.records ?? []),
    [customFields?.records]
  );

  const {
    register,
    watch,
    setValue,
    handleSubmit,
    reset,
    trigger,
    formState: { isSubmitting, errors },
  } = useForm<CandidatesProfileAddSchema>({
    resolver: zodResolver(schema),
    // submit 前に不備が分かるよう、フォーカスアウト時に検証し、以降は入力のたびに再検証する
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      name: defaultValues.name,
      customData: defaultValues.customData,
      organizationUserIds: [],
    },
  });

  /** initialFromAnswer が渡された場合に AnswerItem から算出した値で reset */
  useEffect(() => {
    if (!initialFromAnswer?.length) return;
    const values = buildDefaultValuesFromAnswerItems(initialFromAnswer);
    reset({
      name: values.name,
      customData: values.customData,
      organizationUserIds: [],
    });
  }, [initialFromAnswer, reset]);

  /**
   * 「ファイルから登録」モーダルから遷移してきた場合: atom に保持された解析結果
   * （氏名・customData・blob signed_id 一覧）でフォームをプリフィルする。
   * 反映後は atom を null にクリアし、フォーム再訪問時に古い解析結果が残らないようにする。
   */
  useEffect(() => {
    if (!resumeParseResult) return;
    // CustomDataFormValues は null を許容する汎用型のため、RHF の customData 型（null 非許容）に正規化する
    // （buildCustomDataRecordFromParse / mergeCustomDataRecords は実際には null を生成しない）
    const normalizedCustomData: CandidatesProfileAddSchema['customData'] = Object.fromEntries(
      Object.entries(resumeParseResult.customData).map(([fieldId, value]) => [
        fieldId,
        value ?? undefined,
      ])
    );
    reset({
      name: resumeParseResult.name ?? '',
      customData: normalizedCustomData,
      organizationUserIds: [],
    });
    setParsedBlobSignedIds(resumeParseResult.blobSignedIds);
    setResumeParseResult(null);
  }, [resumeParseResult, reset, setResumeParseResult]);

  /**
   * 書類解析成功時: 解析結果をフォームに反映し、blobのsigned_idを保持する。
   *
   * custom_data の各要素（field_id, content）をフォーム値に変換して setValue する。
   * - multiple_select: content（選択肢 value のカンマ区切り）を選択肢 ID 配列(string[])にマッピングする。
   *   MultiSelect は選択肢 ID 配列を期待するため、value 文字列のままだと選択済み表示にならない。
   * - single_select: Select は選択肢 value をそのまま扱うため content をそのまま設定する。
   * - その他（text/number/date 等）: content をそのまま設定する。
   */
  const handleDocumentParsed = (result: AgentApiCandidateProfilesParseDocument) => {
    // 氏名を自動入力（nullの場合は空文字のまま）
    if (result.name != null) {
      setValue('name', result.name, { shouldValidate: true });
    }
    // カスタムデータを自動入力
    const fieldsById = new Map((customFields?.records ?? []).map((field) => [field.id, field]));
    for (const datum of result.custom_data) {
      const field = fieldsById.get(datum.field_id);
      const fieldKey = `customData.${String(datum.field_id)}` as `customData.${string}`;

      if (field?.field_type === 'multiple_select') {
        // 解析結果の value 文字列（カンマ区切り）を選択肢 ID 配列に変換する
        const values = datum.content
          .split(',')
          .map((v) => v.trim())
          .filter((v) => v !== '');
        const optionIds = field.custom_field_options
          .filter((option) => values.includes(option.value))
          .map((option) => String(option.id));
        setValue(fieldKey, optionIds, { shouldValidate: true });
      } else {
        setValue(fieldKey, datum.content, { shouldValidate: true });
      }
    }
    // blob signed_id を保存（保存時に document_blob_signed_ids として送信）
    setParsedBlobSignedIds([result.blob_signed_id]);
  };

  /** 書類アップロードのリセット時: 解析済み blob_signed_id をクリアする */
  const handleDocumentCleared = () => {
    setParsedBlobSignedIds([]);
  };

  const formValues = watch();
  const customData = formValues.customData || {};
  const name = formValues.name;

  const areRequiredFieldsFilled = useMemo(() => {
    if (!name) return false;
    return areRequiredCustomFieldsFilled(customFields?.records, customData || {});
  }, [name, formValues, customFields?.records]);

  const customFieldErrors = useMemo(() => {
    const errorEntries = errors.customData;
    if (!errorEntries || typeof errorEntries !== 'object') return {};

    return Object.entries(errorEntries).reduce(
      (acc, [fieldId, error]) => {
        if (!error) return acc;
        if ('message' in error && typeof error.message === 'string') {
          acc[fieldId] = error.message;
        }
        return acc;
      },
      {} as Record<string, string | undefined>
    );
  }, [errors.customData]);

  const groupedCustomFields = useMemo(
    () => groupCustomFieldsByCategory(customFields?.records),
    [customFields?.records]
  );

  // クライアント検証に失敗した場合のフィードバック（企業フォームと同挙動）
  const onInvalid = () => {
    showErrorToast('入力内容に不備があります。エラー表示のある項目を確認してください');
    requestAnimationFrame(() => {
      const firstError = document.querySelector('[role="alert"]');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const onSubmit = async (data: CandidatesProfileAddSchema) => {
    try {
      const customDataPayload = buildCustomDataPayload(data.customData, 'create');
      const requestData: CandidateProfilePostRequest = {
        name: data.name,
        organization_user_ids:
          data.organizationUserIds && data.organizationUserIds.length > 0
            ? data.organizationUserIds
            : undefined,
        custom_data: customDataPayload.length > 0 ? customDataPayload : undefined,
        // 書類から作成モード: 解析済みblobのsigned_id一覧を送信してdocument_filesとしてアタッチ
        document_blob_signed_ids: parsedBlobSignedIds.length > 0 ? parsedBlobSignedIds : undefined,
      };
      const res = await mutateAsync(requestData);
      if (candidateFormAnswerId != null) {
        await attachFilesFromAnswer({
          candidateProfileId: res.id,
          candidateFormAnswerId,
        });
      }
      onSuccess?.(res.id);
    } catch (error) {
      // API が返したバリデーション内容（「◯◯は日付で入力してください」等）をそのまま出さないと原因が画面に残らない
      showErrorToast(
        error instanceof Error && error.message ? error.message : '求職者の作成に失敗しました'
      );
    }
  };

  const submitDisabled = isSubmitting || !areRequiredFieldsFilled;

  if (isLoading) {
    return (
      <div
        className={embedInModal ? 'py-8' : 'flex min-h-[200px] items-center justify-center py-12'}
      >
        <Loading size="small" />
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      className={embedInModal ? 'flex flex-col gap-md' : 'relative flex flex-col gap-md p-lg pb-24'}
    >
      {/* 書類から作成モード: アップロードセクションをフォーム上部に表示 */}
      {showResumeUpload && (
        <section className="rounded-sm bg-white p-md">
          <ResumeUploadSection onParsed={handleDocumentParsed} onCleared={handleDocumentCleared} />
        </section>
      )}

      <section className="rounded-sm bg-white p-md">
        <h2 className="text-heading-md text-main">基本情報</h2>
        <div className="mt-md flex flex-wrap items-start gap-md">
          <div className="flex min-w-0 flex-1 shrink-0 flex-col gap-xs sm:max-w-[546px]">
            <label className="m-0 text-heading-xxs text-main">
              氏名
              <span className="ml-xs inline-flex h-5 items-center rounded-full bg-danger_lighten px-sm text-tag-sm-highlight text-danger">
                必須
              </span>
            </label>
            <div>
              <Input
                className="w-full"
                autoComplete="off"
                error={!!errors.name}
                {...register('name')}
              />
              {errors.name && (
                <p role="alert" className="mt-xxs text-body-xs text-danger">
                  {String(errors.name.message)}
                </p>
              )}
            </div>
          </div>
          {activeUsersData && (
            <div className="flex w-full shrink-0 flex-col gap-xs sm:w-[190px]">
              <label className="m-0 text-heading-xxs text-main">担当者</label>
              <MultiSelect
                className="w-full"
                options={activeUsersData.records.map((user) => ({
                  id: user.id,
                  name: user.name,
                }))}
                value={watch('organizationUserIds') ?? []}
                onChange={(vals) => setValue('organizationUserIds', vals, { shouldValidate: true })}
                placeholder="担当者を選択"
              />
            </div>
          )}
        </div>
      </section>

      {groupedCustomFields.map((group) => (
        <section key={group.categoryName} className="rounded-sm bg-white p-md">
          <h2 className="text-heading-md text-main">{group.categoryName}</h2>
          <CustomFieldsForm
            fields={group.customFields}
            values={(customData as Record<string, CustomFieldValue>) || {}}
            onChange={(fieldId, val) =>
              setValue(`customData.${String(fieldId)}` as const, val, { shouldValidate: true })
            }
            onBlur={(fieldId) => trigger(`customData.${String(fieldId)}` as const)}
            registerHiddenInput={(name) => register(name)}
            errors={customFieldErrors}
            disabled={isSubmitting}
          />
        </section>
      ))}

      {embedInModal ? (
        <div className="flex justify-end gap-sm border-t border-solid border-border pt-md">
          {onCancel && (
            <Button color="secondary" size="md" type="button" onClick={onCancel}>
              キャンセル
            </Button>
          )}
          <Button color="primary" size="md" type="submit" disabled={submitDisabled}>
            {isSubmitting ? '作成中...' : '新規作成'}
          </Button>
        </div>
      ) : (
        <div className="fixed right-0 bottom-0 left-[250px] z-10 flex items-center justify-end gap-xs bg-white px-lg py-[14px] shadow-layer3">
          <Button color="primary" size="lg" type="submit" disabled={submitDisabled}>
            {isSubmitting ? '作成中...' : '新規作成'}
          </Button>
        </div>
      )}
    </form>
  );
}
